import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CapacitySighting, LaunchWait } from '../lib/api';
import { formatUsd } from '../lib/api';
import { Alert, Button, Card } from './ui';

/**
 * Launches waiting for AWS capacity, and how the ones that stopped ended.
 *
 * A launch that finds every zone full is not failed - the dashboard keeps
 * asking every 20 seconds for half an hour. This is what that looks like from
 * the operator's chair: which attempt it is on, when the next one is, how long
 * it will keep going, and a way to stop it or start again.
 */

const zone = (az: string) => az.replace(/^.*-(\d[a-z])$/, '$1');

function ago(iso: string, now: number): string {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  return `${Math.round(s / 3600)} h ago`;
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' });
}

/** A clock that ticks once a second while `on`, for countdowns. */
export function useNow(on: boolean): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return now;
}

/**
 * Other sizes a wait may take. Each size draws on its own AWS capacity, so an
 * 8xlarge that is full everywhere can sit beside a 4xlarge or 16xlarge with
 * room - but they are different machines at different prices, so the operator
 * chooses rather than the dashboard.
 */
function Alternatives({ w, onSizes }: { w: LaunchWait; onSizes: (sizeIds: string[]) => void }) {
  const others = w.sizeOptions.filter((s) => s.id !== w.sizeId);
  if (others.length === 0) return null;
  const skipped = new Map(w.skipped.map((k) => [k.sizeId, k.reason]));
  const first = w.sizeOptions.find((s) => s.id === w.sizeId);

  const toggle = (id: string, on: boolean) => {
    const next = w.trying.filter((x) => x !== w.sizeId && x !== id);
    onSizes(on ? [...next, id] : next);
  };
  const all = others.filter((s) => !skipped.has(s.id)).map((s) => s.id);

  return (
    <div className="rounded-md p-3 ring-1 ring-hairline">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-medium">Also accept another size</div>
        <div className="flex gap-1">
          <Button variant="ghost" className="px-2! py-0.5! text-xs" onClick={() => onSizes(all)}>Tick all</Button>
          <Button variant="ghost" className="px-2! py-0.5! text-xs" onClick={() => onSizes([])}>Clear</Button>
        </div>
      </div>
      <div className="mt-0.5 text-xs text-ink-muted">
        Each attempt tries {first?.instanceType ?? 'your first choice'} first, then every size ticked
        here, in every zone. Whichever has room first launches.
      </div>
      <div className="mt-2 space-y-1.5">
        {others.map((s) => {
          const reason = skipped.get(s.id);
          const cheaper = first?.usdPerHour != null && s.usdPerHour != null && s.usdPerHour < first.usdPerHour;
          return (
            <label key={s.id}
                   className={`flex items-start gap-2 text-xs ${reason ? 'opacity-60' : 'cursor-pointer'}`}>
              <input type="checkbox" className="mt-0.5" disabled={!!reason}
                     checked={w.trying.includes(s.id)}
                     onChange={(e) => toggle(s.id, e.target.checked)} />
              <span>
                <span className="font-medium">{s.label}</span>
                <span className="text-ink-secondary">
                  {' '}· {s.instanceType} · {s.vcpu} vCPU{s.gpu ? ` · ${s.gpu}` : ''}
                  {s.usdPerHour != null && ` · ${formatUsd(s.usdPerHour, 2)}/hr`}
                  {cheaper ? ' (cheaper)' : ''}
                </span>
                {reason && <span className="block" style={{ color: 'var(--color-warning)' }}>⚠ {reason}</span>}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function Waiting({ w, now, onCancel, onPickAnother, onSizes }: {
  w: LaunchWait;
  now: number;
  onCancel: () => void;
  onPickAnother: () => void;
  onSizes: (sizeIds: string[]) => void;
}) {
  const start = Date.parse(w.startedAt);
  const end = Date.parse(w.until);
  const pct = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
  const next = w.nextAttemptAt ? Math.max(0, Math.ceil((Date.parse(w.nextAttemptAt) - now) / 1000)) : null;

  return (
    <Card title="Waiting for AWS capacity">
      <div className="space-y-3 p-4 text-sm">
        <div>
          <span className="font-medium">{w.name}</span>
          <span className="text-ink-secondary"> · {w.sizeLabel} ({w.instanceType}) · {w.templateName}</span>
        </div>

        <div className="text-ink-secondary">
          AWS has no {w.instanceType} free in any of the {w.zones} zone{w.zones === 1 ? '' : 's'} right
          now. The dashboard asks again every 20 seconds and launches it the moment one has room.
        </div>

        {/* Time used of the half hour it will keep trying. */}
        <div>
          <div className="h-1.5 overflow-hidden rounded bg-plane" role="progressbar"
               aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full bg-series-1 transition-[width] duration-1000" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1.5 flex flex-wrap justify-between gap-2 text-xs text-ink-muted tabular">
            <span>
              Attempt {w.attempts} · {next === null ? 'asking AWS now…' : `next in ${next}s`}
            </span>
            <span>keeps trying until {clock(w.until)}</span>
          </div>
        </div>

        <Alternatives w={w} onSizes={onSizes} />

        <div className="text-xs text-ink-muted">
          You can leave this page. It launches by itself, and the instance appears under Instances.
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={onCancel}>Stop waiting</Button>
          <Button variant="ghost" onClick={onPickAnother}>Stop and pick another size</Button>
        </div>
      </div>
    </Card>
  );
}

export function LaunchWaits({ waits, now, onCancel, onRetry, onDismiss, onPickAnother, onSizes }: {
  waits: LaunchWait[];
  now: number;
  onCancel: (w: LaunchWait) => void;
  onSizes: (w: LaunchWait, sizeIds: string[]) => void;
  /** Launch the same machine again. */
  onRetry: (w: LaunchWait) => void;
  onDismiss: (w: LaunchWait) => void;
  onPickAnother: (w: LaunchWait) => void;
}) {
  if (waits.length === 0) return null;

  return (
    <div className="space-y-3">
      {waits.map((w) => {
        if (w.status === 'waiting') {
          return (
            <Waiting key={w.id} w={w} now={now} onSizes={(ids) => onSizes(w, ids)}
                     onCancel={() => onCancel(w)} onPickAnother={() => onPickAnother(w)} />
          );
        }

        const actions = (
          <div className="mt-2 flex flex-wrap gap-2">
            {w.status === 'launched' && w.instanceId ? (
              <Link to={`/machines/${w.instanceId}`}><Button variant="primary">Open machine</Button></Link>
            ) : (
              <Button variant="primary" onClick={() => onRetry(w)}>Try again</Button>
            )}
            <Button variant="ghost" onClick={() => onDismiss(w)}>Dismiss</Button>
          </div>
        );

        if (w.status === 'launched') {
          return (
            <Alert key={w.id} tone="good" title={`${w.name} launched`}>
              {w.message}.{actions}
            </Alert>
          );
        }
        return (
          <Alert key={w.id} tone={w.status === 'failed' ? 'critical' : 'info'}
                 title={w.status === 'failed' ? `${w.name} did not launch` : `Stopped waiting for ${w.name}`}>
            {w.message}{w.message.endsWith('.') ? '' : '.'}{actions}
          </Alert>
        );
      })}
    </div>
  );
}

/**
 * What recent launches and starts found for this size, zone by zone.
 *
 * AWS publishes no on-demand capacity figure, so this is the nearest honest
 * thing: the answer AWS gave the last time this dashboard asked. Old news is
 * labelled as old, and a size nobody has launched recently shows nothing
 * rather than a guess.
 */
export function CapacityLine({ seen, now }: { seen?: CapacitySighting[]; now: number }) {
  if (!seen || seen.length === 0) return null;
  const allFull = seen.every((s) => !s.ok);

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
         style={{ color: allFull ? 'var(--color-warning)' : undefined }}>
      <span className={allFull ? '' : 'text-ink-muted'}>
        {allFull ? '⚠ No room at last check:' : 'Last checked:'}
      </span>
      {[...seen].sort((a, b) => a.az.localeCompare(b.az)).map((s) => (
        <span key={s.az} className="tabular"
              style={{ color: s.ok ? 'var(--color-good)' : 'var(--color-warning)' }}>
          {s.ok ? '✓' : '✕'} {zone(s.az)} {s.ok ? 'had room' : 'full'} {ago(s.at, now)}
        </span>
      ))}
    </div>
  );
}
